"use client"

import type React from "react"
import { memo, useEffect, useRef, useState } from "react"
import Image from "next/image"
import { m } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { ClientOnly } from "@/lib/animation/client-only"
import { cn } from "@/lib/utils"
import { MRI_USP_STEPS, type MriUspStep } from "@/data/mri-sections"
import { PlaceholderMedia } from "@/components/ui/placeholder-media"
import { CheckmarkIcon } from "@/components/ui/icons"
import { blobMediaUrl } from "@/lib/blob-url"

// ds-typo-ignore-file — the datapoint chips and overlay replicate the report
// viewer UI exactly (intentional sub-DS px sizes), like the report-mockup-* set.

function UspMedia({
  step,
  priority,
  sizes,
  className,
}: {
  step: MriUspStep
  priority?: boolean
  sizes: string
  className?: string
}) {
  if (!step.image) {
    return (
      <PlaceholderMedia
        label={step.placeholderLabel}
        className={cn("h-full w-full", className)}
      />
    )
  }
  return (
    <Image
      src={blobMediaUrl(step.image)}
      alt=""
      fill
      priority={priority}
      sizes={sizes}
      className={cn("object-cover", className)}
    />
  )
}

/**
 * "Why our scan" — sticky-image scrollytelling across the 3 scan USPs.
 * Mirrors the /membership page's `MembershipYear` section: a sticky image
 * card that crossfades per step with an animated overlay card floating on
 * it, beside a text column of badge + title + body. Active step is the
 * one whose progress dot sits inside the image's vertical band.
 *
 * Images and the floating overlay card are placeholders (AHD-6064 assets).
 */

/* Pill-and-dots progress indicator — active step morphs into a pill. */
const ProgressDot = memo(
  function ProgressDot({
    isActive,
    onClick,
    ariaLabel,
  }: {
    isActive: boolean
    onClick: () => void
    ariaLabel: string
  }) {
    return (
      <m.button
        layout
        type="button"
        data-testid="usp-progress-dot"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-current={isActive}
        className="h-[8px] cursor-pointer rounded-full will-change-transform"
        animate={{
          width: isActive ? 32 : 8,
          backgroundColor: isActive ? "#d4d4d4" : "#e5e5e5",
        }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      />
    )
  },
  (prev, next) => prev.isActive === next.isActive
)

/* Placeholder for the floating overlay card that animates over each image
   — a translucent glass card with a "Placeholder" marker and skeleton
   rows. Swap for the real overlay component once designed (AHD-6064). */
function UspOverlayCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col gap-3 rounded-[16px] border border-white/25 bg-black/35 p-4 backdrop-blur-[8px]",
        className
      )}
    >
      <span className="type-label-sm inline-flex self-start rounded-full bg-white/15 px-2.5 py-1 text-white/80 uppercase">
        Placeholder
      </span>
      <div className="h-2.5 w-3/4 rounded-full bg-white/30" />
      <div className="h-2.5 w-1/2 rounded-full bg-white/20" />
      <div className="h-2.5 w-2/3 rounded-full bg-white/20" />
    </div>
  )
}

/* Range-chart fill colours — tokens live in `app/globals.css`. */
type RangeBarColor = "terracotta-300" | "light-green-400" | "sand-400"
type RangeBar = { y: number; height: number; color: RangeBarColor }
type RangeDot = { leftPct: number; topPct: number }

function rangeBarFill(c: RangeBarColor): string {
  switch (c) {
    case "terracotta-300":
      return "var(--color-terracotta-300)"
    case "light-green-400":
      return "var(--color-light-green-400)"
    case "sand-400":
      return "var(--color-sand-400)"
  }
}

/* 143×79 range chart mirroring the report's per-biomarker dataPoint chart.
   Geometry is passed in so each chip renders its own gradient band, side
   bars, cohort dots, and "you" marker. */
function BiomarkerRangeChart({
  gradient,
  bars,
  dots,
  youMarker,
}: {
  gradient: { top: number; height: number }
  bars: RangeBar[]
  dots: RangeDot[]
  youMarker: RangeDot
}) {
  // Chart geometry was authored against an 79px-tall chart; scale it down so
  // the chip height matches the 56px thumbnail for balanced, even padding.
  const SCALE = 56 / 79
  return (
    <div
      className="relative shrink-0 overflow-visible"
      style={{ width: 143, height: 56 }}
    >
      <div
        className="absolute top-0 right-0 bottom-0 left-0 rounded"
        style={{ inset: "0px 12px 0px 0px" }}
      >
        <div
          className="to-light-green-100 absolute right-0 left-0 rounded bg-gradient-to-r from-transparent"
          style={{ top: gradient.top * SCALE, height: gradient.height * SCALE }}
        />
        <svg
          className="absolute overflow-visible"
          width="5"
          height="56"
          style={{ right: 2, top: 0 }}
        >
          <g>
            {bars.map((b, i) => (
              <rect
                key={i}
                x="0"
                y={b.y * SCALE}
                width="5"
                height={b.height * SCALE}
                rx="2.5"
                fill={rangeBarFill(b.color)}
              />
            ))}
          </g>
        </svg>
      </div>
      <div
        className="absolute border-l border-dashed border-neutral-700 opacity-30"
        style={{ left: "75%", top: 0, bottom: 0 }}
      />
      {dots.map((d, i) => (
        <div
          key={i}
          className="absolute flex items-center justify-center rounded-full bg-neutral-900 opacity-50"
          style={{
            width: 10,
            height: 10,
            left: `${d.leftPct}%`,
            top: `${d.topPct}%`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <div className="h-1 w-1 rounded-full bg-white" />
        </div>
      ))}
      <div
        className="absolute rounded-full bg-neutral-900"
        style={{
          width: 10,
          height: 10,
          left: `${youMarker.leftPct}%`,
          top: `${youMarker.topPct}%`,
          transform: "translate(-50%, -50%)",
        }}
      >
        <div
          className="absolute top-1/2 left-1/2 rounded-full bg-white"
          style={{ width: 4, height: 4, transform: "translate(-50%, -50%)" }}
        />
      </div>
    </div>
  )
}

/* AI-datapoint chip used on mriUsp3 — glass shell (same translucent
   treatment as the other overlay chips) with a 48px MRI source thumbnail on the left, the
   biomarker title + value/unit + Normal pill in the middle, and the
   per-biomarker range chart on a second row. All text in DS neutral-900
   so it reads on the lighter MRI-machine background. */
function DatapointChip({
  image,
  title,
  value,
  unit,
  statusLabel,
  chart,
  className,
}: {
  image: string
  title: string
  value: string
  unit: string
  statusLabel: string
  chart: {
    gradient: { top: number; height: number }
    bars: RangeBar[]
    dots: RangeDot[]
    youMarker: RangeDot
  }
  className?: string
}) {
  return (
    <div
      role="article"
      aria-label={title}
      className={cn(
        "pointer-events-none flex flex-col gap-[10px] rounded-[20px] border-[0.4px] border-white/30 bg-white/35 p-[12px] shadow-none backdrop-blur-[6px] md:flex-row md:items-center md:gap-[14px]",
        className
      )}
    >
      <div className="flex items-start gap-[12px] md:min-w-0 md:flex-1">
        <div className="relative size-[56px] shrink-0 overflow-hidden rounded-[12px] bg-black">
          <Image
            src={blobMediaUrl(image)}
            alt=""
            fill
            sizes="56px"
            className="object-cover"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
          <p className="text-[13px] leading-[16px] font-semibold tracking-[-0.2px] text-neutral-900">
            {title}
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-[18px] leading-none font-medium text-neutral-900">
              {value}
            </span>
            <span className="pb-[1px] font-mono text-[9px] leading-3 font-normal tracking-wider text-neutral-900">
              {unit}
            </span>
          </div>
          <span
            role="status"
            className="text-light-green-800 bg-light-green-200/85 mt-0.5 w-fit truncate rounded-[40px] px-2 py-0.5 text-[10px] leading-[12px] font-medium text-nowrap"
          >
            {statusLabel}
          </span>
        </div>
      </div>
      <div className="self-end md:shrink-0 md:self-center">
        <BiomarkerRangeChart {...chart} />
      </div>
    </div>
  )
}

/* mriUsp3 overlay — three stacked AI-extracted datapoint chips (brain
   volumetry, liver PDFF, liver R2*). Images are uploaded versions of the
   firebase source files; chart geometry mirrors the live report UI per
   biomarker. Copy hardcoded pending i18n. */
function AiBiomarkersOverlay() {
  const tt = useTranslations("tests") as unknown as (k: string) => string
  return (
    <div className="flex flex-col gap-[8px]">
      <DatapointChip
        image="/images/mri/datapoint-cortical-gray-matter.png"
        title={tt("mriUspBiomarkerBrain")}
        value="34.2"
        unit="%"
        statusLabel={tt("mriUspStatusNormal")}
        chart={{
          gradient: { top: 23.1, height: 15.7 },
          bars: [
            { y: 40.8, height: 35.2, color: "terracotta-300" },
            { y: 23.1, height: 15.7, color: "light-green-400" },
            { y: 3, height: 18.1, color: "terracotta-300" },
          ],
          dots: [
            { leftPct: 51, topPct: 26.37 },
            { leftPct: 63, topPct: 43.05 },
          ],
          youMarker: { leftPct: 75, topPct: 43.05 },
        }}
      />
      <DatapointChip
        image="/images/mri/datapoint-liver-pdff.png"
        title={tt("mriUspBiomarkerLiverFat")}
        value="1.1"
        unit="%"
        statusLabel={tt("mriUspStatusNormal")}
        chart={{
          gradient: { top: 60, height: 16 },
          bars: [
            { y: 60, height: 16, color: "light-green-400" },
            { y: 3, height: 55, color: "sand-400" },
          ],
          dots: [{ leftPct: 63, topPct: 89.11 }],
          youMarker: { leftPct: 75, topPct: 92.49 },
        }}
      />
      <DatapointChip
        image="/images/mri/datapoint-liver-r2.png"
        title={tt("mriUspBiomarkerLiverIron")}
        value="40"
        unit="s⁻¹"
        statusLabel={tt("mriUspStatusNormal")}
        chart={{
          gradient: { top: 49.25, height: 26.75 },
          bars: [
            { y: 49.25, height: 26.75, color: "light-green-400" },
            { y: 3, height: 44.25, color: "terracotta-300" },
          ],
          dots: [{ leftPct: 63, topPct: 72.65 }],
          youMarker: { leftPct: 75, topPct: 84.57 },
        }}
      />
    </div>
  )
}

/* mriUsp2 overlay — a single glass "standards" card. Replaces the old
   notification-style chips (which cloned the membership push notification:
   app-logo avatar + brand line + message line) with a credential checklist
   that reads as the research standards the scan is built on. White text +
   DS GreenCheck on the darkened scan image. Copy hardcoded pending i18n
   (AHD-6064 deferred — same status as the section body strings). */
const RESEARCH_STANDARDS = [
  "mriUspStandard1",
  "mriUspStandard2",
  "mriUspStandard3",
]

function ResearchStandardsCard() {
  const tt = useTranslations("tests") as unknown as (k: string) => string
  return (
    <div className="pointer-events-none flex flex-col gap-3 rounded-[16px] bg-white p-5 shadow-[0_12px_32px_-12px_rgba(0,0,0,0.4)]">
      <p className="text-[13px] leading-[16px] font-semibold tracking-[-0.2px] text-neutral-900">
        {tt("mriUspStandardsHeading")}
      </p>
      <ul className="flex flex-col gap-2.5">
        {RESEARCH_STANDARDS.map((standard) => (
          <li key={standard} className="flex items-center gap-2.5">
            <CheckmarkIcon
              width={18}
              height={18}
              className="text-deep-green-700 shrink-0"
            />
            <span className="text-[13px] leading-[16px] tracking-[-0.2px] text-neutral-700">
              {tt(standard)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* Per-step overlay dispatcher. Returns the real overlay for steps that
   have one designed, falls back to the placeholder card for the rest. */
function UspStepOverlay({ step }: { step: MriUspStep }) {
  // mriUsp1 intentionally has no overlay chip — the cortical-gray-matter
  // stat card was removed per review feedback.
  if (step.keyBase === "mriUsp1") return null
  if (step.keyBase === "mriUsp2") {
    return <ResearchStandardsCard />
  }
  if (step.keyBase === "mriUsp3") {
    return <AiBiomarkersOverlay />
  }
  return <UspOverlayCard />
}

export function ScanUspScrollytelling() {
  const tt = useTranslations("tests") as unknown as (k: string) => string
  const [active, setActive] = useState(0)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const dotRefs = useRef<(HTMLDivElement | null)[]>([])
  const imageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // A step is active while its dot is inside the image's vertical band —
    // the image swaps as the dot crosses the band edge. rAF-throttled,
    // no-op-guarded. (Cloned from MembershipYear.)
    let rafId = 0
    let lastActive = -1
    const compute = () => {
      rafId = 0
      const img = imageRef.current
      if (!img) return
      const r = img.getBoundingClientRect()
      const vh = window.innerHeight
      if (r.bottom < 0 || r.top > vh) return
      for (let i = 0; i < dotRefs.current.length; i++) {
        const dot = dotRefs.current[i]
        if (!dot) continue
        const dr = dot.getBoundingClientRect()
        const dotY = dr.top + dr.height / 2
        if (dotY >= r.top && dotY <= r.bottom) {
          if (lastActive !== i) {
            lastActive = i
            setActive(i)
          }
          return
        }
      }
    }
    const onScroll = () => {
      if (rafId === 0) rafId = requestAnimationFrame(compute)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    compute()
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (rafId !== 0) cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <section id="mri-scan-usps" className="bg-neutral-50 py-16 md:py-20">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="mx-auto mb-4 max-w-[620px] text-center md:mb-6">
          <h2 className="type-display-md text-neutral-900">
            {tt("mriUspHeading")}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-16">
          {/* Sticky image card + progress dots */}
          <div className="hidden md:block">
            {/* Centering band is capped (`min(100vh, 800px)`) so on tall
                displays the first step sits just below the section header
                instead of being pushed half a viewport down. Laptops ≤800px
                tall fall back to the full-viewport behaviour unchanged. */}
            <div className="sticky top-0 flex min-h-[min(100vh,800px)] flex-col justify-center py-8">
              <div
                ref={imageRef}
                className="relative aspect-[8/7] w-full overflow-hidden rounded-[24px] bg-neutral-200"
              >
                {MRI_USP_STEPS.map((s, i) => (
                  <m.div
                    key={s.keyBase}
                    className="absolute inset-0 will-change-[opacity,transform]"
                    initial={false}
                    animate={{
                      opacity: i === active ? 1 : 0,
                      scale: i === active ? 1 : 1.02,
                    }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <UspMedia
                      step={s}
                      priority={i === 0}
                      sizes="(min-width: 768px) 50vw, 100vw"
                    />
                  </m.div>
                ))}
                {/* Darken the scan image so the translucent datapoint chips
                    read with enough contrast. */}
                <div className="pointer-events-none absolute inset-0 bg-black/25" />
                {/* Overlay hoisted out of the opacity-animating wrapper, AND
                    the overlay itself renders without an opacity entrance —
                    any ancestor with opacity < 1 suppresses descendant
                    backdrop-filter, making the glass blur "pop in" once the
                    animation finishes. Re-mounting on `active` change still
                    swaps the overlay; we just skip the fade. */}
                <ClientOnly>
                  <div
                    key={`overlay-${active}`}
                    className="absolute bottom-[40px] left-[40px] w-[300px] max-w-[calc(100%-80px)] lg:w-[420px]"
                  >
                    <UspStepOverlay step={MRI_USP_STEPS[active]} />
                  </div>
                </ClientOnly>
              </div>

              <div className="mx-auto mt-6 flex w-1/4 items-center justify-center gap-2">
                {MRI_USP_STEPS.map((s, i) => (
                  <ProgressDot
                    key={s.keyBase}
                    isActive={i === active}
                    ariaLabel={tt(`${s.keyBase}Title`)}
                    onClick={() =>
                      stepRefs.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      })
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Text column */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute top-0 bottom-0 left-0 hidden w-px bg-neutral-200 md:block"
              style={{
                maskImage:
                  "linear-gradient(to bottom, transparent 0%, black 6%, black 94%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent 0%, black 6%, black 94%, transparent 100%)",
              }}
            />

            {MRI_USP_STEPS.map((s, i) => (
              <div
                key={s.keyBase}
                ref={(el) => {
                  stepRefs.current[i] = el
                }}
                className="relative py-10 md:min-h-[128vh] md:py-0"
              >
                <div className="md:sticky md:top-0 md:flex md:h-[min(100vh,800px)] md:flex-col md:justify-center">
                  {/* Mobile-only inline image + overlay */}
                  <div className="relative mb-6 aspect-square w-full overflow-hidden rounded-[24px] bg-neutral-200 md:hidden">
                    <UspMedia step={s} priority={i === 0} sizes="100vw" />
                    {/* No opacity entrance — see desktop overlay note. */}
                    <div className="absolute bottom-[16px] left-[16px] w-[260px] max-w-[calc(100%-32px)]">
                      <UspStepOverlay step={s} />
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 md:gap-5 md:pl-16">
                    <span className="type-label-lg bg-deep-green-100 text-deep-green-900 inline-flex self-start rounded-full px-3 py-1 normal-case">
                      {tt("mriUspLabel")}
                    </span>

                    <div
                      className="relative"
                      style={{ lineHeight: "var(--leading-display-sm)" }}
                    >
                      <m.div
                        ref={(el) => {
                          dotRefs.current[i] = el
                        }}
                        aria-hidden="true"
                        className="absolute left-[-67px] hidden h-[8px] w-[8px] rounded-full ring-4 ring-neutral-50 md:block"
                        style={{ top: "calc(0.5lh - 4px)" }}
                        animate={
                          i === active
                            ? {
                                backgroundColor: "var(--color-neutral-200)",
                                scale: [1, 1.3, 1],
                              }
                            : {
                                backgroundColor: "var(--color-neutral-100)",
                                scale: 1,
                              }
                        }
                        transition={
                          i === active
                            ? {
                                backgroundColor: {
                                  duration: 0.4,
                                  ease: [0.16, 1, 0.3, 1],
                                },
                                scale: {
                                  duration: 1.6,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                },
                              }
                            : { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
                        }
                      />
                      <h3 className="type-display-sm text-neutral-900">
                        {tt(`${s.keyBase}Title`)}
                      </h3>
                    </div>

                    <p className="type-body-lg max-w-[480px] text-neutral-600">
                      {tt(`${s.keyBase}Body`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
